import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface FeeType {
  id: string;
  name: string;
  active: boolean;
}

interface Currency {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

interface Property {
  id: string;
  code: string;
  street: string;
  active: boolean;
}

interface UnitFeeConfig {
  feeTypeId: string;
  currencyId: string;
  daily: number;
  weekly: number;
  monthly: number;
  bimonthly: number;
  quarterly: number;
  semiAnnual: number;
  annual: number;
  oneTime: number;
}

interface UnitCreateStandaloneDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const UnitCreateStandaloneDialog: React.FC<UnitCreateStandaloneDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    propertyId: '',
    number: '',
    rooms: 1,
    bathrooms: 1,
    floor: 1,
    areaM2: 0,
    includeFees: false
  });

  const [feeConfigs, setFeeConfigs] = useState<UnitFeeConfig[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [feeTypes, setFeeTypes] = useState<FeeType[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [loadingFeeTypes, setLoadingFeeTypes] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();

  // Fetch properties, fee types and currencies when dialog opens
  useEffect(() => {
    if (open) {
      fetchProperties();
      fetchFeeTypes();
      fetchCurrencies();
    }
  }, [open]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        propertyId: '',
        number: '',
        rooms: 1,
        bathrooms: 1,
        floor: 1,
        areaM2: 0,
        includeFees: false
      });
      setFeeConfigs([]);
      setErrors({});
    }
  }, [open]);

  const fetchProperties = async () => {
    setLoadingProperties(true);
    try {
      const response = await DataService.get('api/property/all');

      if (response.ok) {
        const responseData = await response.json();

        let propertyList: Property[] = [];

        // Handle different response formats
        if (responseData.success && responseData.data) {
          propertyList = responseData.data.filter((p: Property) => p.active);
        } else if (Array.isArray(responseData)) {
          propertyList = responseData.filter((p: Property) => p.active);
        } else if (responseData.success && Array.isArray(responseData.result)) {
          propertyList = responseData.result.filter((p: Property) => p.active);
        } else if (Array.isArray(responseData.result)) {
          propertyList = responseData.result.filter((p: Property) => p.active);
        }

        setProperties(propertyList);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      dispatch(showAlert({
        message: 'Failed to load properties',
        type: 'error'
      }));
    } finally {
      setLoadingProperties(false);
    }
  };

  const fetchFeeTypes = async () => {
    setLoadingFeeTypes(true);
    try {
      const response = await DataService.get('api/fee-types/all');

      if (response.ok) {
        const responseData = await response.json();

        let feeTypeList: FeeType[] = [];

        if (responseData.success && responseData.data) {
          feeTypeList = responseData.data.filter((ft: FeeType) => ft.active);
        } else if (Array.isArray(responseData)) {
          feeTypeList = responseData.filter((ft: FeeType) => ft.active);
        } else if (responseData.success && Array.isArray(responseData.result)) {
          feeTypeList = responseData.result.filter((ft: FeeType) => ft.active);
        } else if (Array.isArray(responseData.result)) {
          feeTypeList = responseData.result.filter((ft: FeeType) => ft.active);
        }

        setFeeTypes(feeTypeList);
      }
    } catch (error) {
      console.error('Error fetching fee types:', error);
      dispatch(showAlert({
        message: 'Failed to load fee types',
        type: 'error'
      }));
    } finally {
      setLoadingFeeTypes(false);
    }
  };

  const fetchCurrencies = async () => {
    setLoadingCurrencies(true);
    try {
      const response = await DataService.get('api/Currency/all');

      if (response.ok) {
        const responseData = await response.json();

        let currencyList: Currency[] = [];

        if (responseData.success && responseData.data) {
          currencyList = responseData.data.filter((c: Currency) => c.active);
        } else if (Array.isArray(responseData)) {
          currencyList = responseData.filter((c: Currency) => c.active);
        } else if (responseData.success && Array.isArray(responseData.result)) {
          currencyList = responseData.result.filter((c: Currency) => c.active);
        } else if (Array.isArray(responseData.result)) {
          currencyList = responseData.result.filter((c: Currency) => c.active);
        }

        setCurrencies(currencyList);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      dispatch(showAlert({
        message: 'Failed to load currencies',
        type: 'error'
      }));
    } finally {
      setLoadingCurrencies(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const addFeeConfig = () => {
    const newFeeConfig: UnitFeeConfig = {
      feeTypeId: '',
      currencyId: '',
      daily: 0,
      weekly: 0,
      monthly: 0,
      bimonthly: 0,
      quarterly: 0,
      semiAnnual: 0,
      annual: 0,
      oneTime: 0
    };
    setFeeConfigs([...feeConfigs, newFeeConfig]);
  };

  const removeFeeConfig = (index: number) => {
    setFeeConfigs(feeConfigs.filter((_, i) => i !== index));
  };

  const updateFeeConfig = (index: number, field: keyof UnitFeeConfig, value: any) => {
    const updated = [...feeConfigs];
    updated[index] = { ...updated[index], [field]: value };
    setFeeConfigs(updated);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.propertyId) {
      newErrors.propertyId = 'Property is required';
    }

    if (!formData.number.trim()) {
      newErrors.number = 'Unit number is required';
    }

    if (formData.rooms < 0) {
      newErrors.rooms = 'Rooms must be a positive number';
    }

    if (formData.bathrooms < 0) {
      newErrors.bathrooms = 'Bathrooms must be a positive number';
    }

    if (formData.floor < 0) {
      newErrors.floor = 'Floor must be a positive number';
    }

    if (formData.areaM2 <= 0) {
      newErrors.areaM2 = 'Area must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const payload = [{
        propertyId: formData.propertyId,
        number: formData.number.trim(),
        rooms: formData.rooms,
        bathrooms: formData.bathrooms,
        floor: formData.floor,
        areaM2: formData.areaM2,
        includeFees: formData.includeFees,
        includeAppliances: false,
        unitFeeConfigs: formData.includeFees
          ? feeConfigs
              .filter(fc => fc.feeTypeId && fc.currencyId)
              .map(fc => ({
                feeTypeId: fc.feeTypeId,
                currencyId: fc.currencyId,
                daily: fc.daily,
                weekly: fc.weekly,
                monthly: fc.monthly,
                bimonthly: fc.bimonthly,
                quarterly: fc.quarterly,
                semiAnnual: fc.semiAnnual,
                annual: fc.annual,
                oneTime: fc.oneTime
              }))
          : [],
        appliances: []
      }];

      const response = await DataService.post('api/unit/create', payload);

      if (response.ok) {
        dispatch(showAlert({
          message: 'Unit created successfully!',
          type: 'success'
        }));
        onSuccess();
      } else {
        let errorMessage = 'Failed to create unit';
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          errorMessage = `Failed to create unit: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating unit:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create unit. Please try again.';
      dispatch(showAlert({
        message: errorMessage,
        type: 'error'
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '50vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          Create New Unit
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add a new unit to a property
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Property Selection */}
          <Card variant="outlined">
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', mb: 1 }}>
                <BusinessIcon color="primary" fontSize="small" />
                Property Selection
              </Typography>

              <FormControl fullWidth error={!!errors.propertyId}>
                <InputLabel>Property *</InputLabel>
                <Select
                  value={formData.propertyId}
                  label="Property *"
                  onChange={(e) => handleInputChange('propertyId', e.target.value)}
                  disabled={submitting || loadingProperties}
                >
                  <MenuItem value="">
                    <em>Select a property</em>
                  </MenuItem>
                  {properties.map((property) => (
                    <MenuItem key={property.id} value={property.id}>
                      {property.code} - {property.street}
                    </MenuItem>
                  ))}
                </Select>
                {errors.propertyId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.propertyId}
                  </Typography>
                )}
              </FormControl>
            </CardContent>
          </Card>

          {/* Unit Information */}
          <Card variant="outlined">
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', mb: 1 }}>
                <HomeIcon color="primary" fontSize="small" />
                Unit Information
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Unit Number"
                    value={formData.number}
                    onChange={(e) => handleInputChange('number', e.target.value)}
                    error={!!errors.number}
                    helperText={errors.number}
                    disabled={submitting}
                    required
                    placeholder="e.g., u12"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Floor"
                    type="number"
                    value={formData.floor}
                    onChange={(e) => handleInputChange('floor', parseInt(e.target.value) || 0)}
                    error={!!errors.floor}
                    helperText={errors.floor}
                    disabled={submitting}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Rooms"
                    type="number"
                    value={formData.rooms}
                    onChange={(e) => handleInputChange('rooms', parseInt(e.target.value) || 0)}
                    error={!!errors.rooms}
                    helperText={errors.rooms}
                    disabled={submitting}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Bathrooms"
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => handleInputChange('bathrooms', parseInt(e.target.value) || 0)}
                    error={!!errors.bathrooms}
                    helperText={errors.bathrooms}
                    disabled={submitting}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Area (m²)"
                    type="number"
                    value={formData.areaM2}
                    onChange={(e) => handleInputChange('areaM2', parseFloat(e.target.value) || 0)}
                    error={!!errors.areaM2}
                    helperText={errors.areaM2}
                    disabled={submitting}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.includeFees}
                        onChange={(e) => handleInputChange('includeFees', e.target.checked)}
                        disabled={submitting}
                      />
                    }
                    label="Include Fees"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Fee Configurations */}
          <Collapse in={formData.includeFees}>
            <Card variant="outlined">
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                    <AttachMoneyIcon color="primary" fontSize="small" />
                    Fee Configurations (Optional)
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addFeeConfig}
                    disabled={submitting}
                  >
                    Add Fee
                  </Button>
                </Box>

                {feeConfigs.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 1, display: 'block' }}>
                    No fee configurations added. Click "Add Fee" to add one.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {feeConfigs.map((feeConfig, index) => (
                      <Card key={index} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                        <CardContent sx={{ p: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" fontWeight="bold">
                              Fee Config #{index + 1}
                            </Typography>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeFeeConfig(index)}
                              disabled={submitting}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Fee Type *</InputLabel>
                                <Select
                                  value={feeConfig.feeTypeId}
                                  label="Fee Type *"
                                  onChange={(e) => updateFeeConfig(index, 'feeTypeId', e.target.value)}
                                  disabled={submitting || loadingFeeTypes}
                                >
                                  <MenuItem value="">
                                    <em>Select a fee type</em>
                                  </MenuItem>
                                  {feeTypes.map((feeType) => (
                                    <MenuItem key={feeType.id} value={feeType.id}>
                                      {feeType.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Currency *</InputLabel>
                                <Select
                                  value={feeConfig.currencyId}
                                  label="Currency *"
                                  onChange={(e) => updateFeeConfig(index, 'currencyId', e.target.value)}
                                  disabled={submitting || loadingCurrencies}
                                >
                                  <MenuItem value="">
                                    <em>Select a currency</em>
                                  </MenuItem>
                                  {currencies.map((currency) => (
                                    <MenuItem key={currency.id} value={currency.id}>
                                      {currency.code} - {currency.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Daily"
                                type="number"
                                value={feeConfig.daily}
                                onChange={(e) => updateFeeConfig(index, 'daily', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Weekly"
                                type="number"
                                value={feeConfig.weekly}
                                onChange={(e) => updateFeeConfig(index, 'weekly', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Monthly"
                                type="number"
                                value={feeConfig.monthly}
                                onChange={(e) => updateFeeConfig(index, 'monthly', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Bimonthly"
                                type="number"
                                value={feeConfig.bimonthly}
                                onChange={(e) => updateFeeConfig(index, 'bimonthly', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Quarterly"
                                type="number"
                                value={feeConfig.quarterly}
                                onChange={(e) => updateFeeConfig(index, 'quarterly', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Semi-Annual"
                                type="number"
                                value={feeConfig.semiAnnual}
                                onChange={(e) => updateFeeConfig(index, 'semiAnnual', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Annual"
                                type="number"
                                value={feeConfig.annual}
                                onChange={(e) => updateFeeConfig(index, 'annual', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={4} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="One Time"
                                type="number"
                                value={feeConfig.oneTime}
                                onChange={(e) => updateFeeConfig(index, 'oneTime', parseFloat(e.target.value) || 0)}
                                disabled={submitting}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Collapse>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          variant="outlined"
          size="small"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          variant="contained"
          size="small"
          startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {submitting ? 'Creating...' : 'Create Unit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnitCreateStandaloneDialog;
